/**
 * AP-28 9.1 — die Artikelliste nach dem Listen-Standard.
 *
 * `FilterBar`, Spalten-Konfiguration, Sortierung, Paginierung, `HelpPopover`,
 * `LayoutDefaultActions`. Die Einkaufsspalten tragen `requiresPermission` —
 * gestrichen an **einer** Stelle (`ohneFehlendeRechte`). Das bleibt Kosmetik:
 * Die Sperre ist die Beschneidung der Antwort im Backend.
 *
 * Kein Datum in der Vorbelegung: Ein Artikel ist kein Ereignis.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Settings, Check, ArrowUp, ArrowDown, ArrowUpDown, ListTree, Upload } from 'lucide-react'
import { ExportButton } from '@/components/ui/ExportButton'
import { erfuelltRecht } from '@symphenia/shared-types'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { ohneFehlendeRechte } from '@/components/list/list.types'
import type { FilterFieldDef, ActiveFilter, ColumnFieldDef } from '@/components/list/list.types'
import { pageWide } from '@/lib/form-styles'
import { useAuthStore } from '@/lib/auth.store'
import { articlesApi } from '../api/articles.api'
import { ArtAbzeichen, ArtSymbol, RollenKuerzel } from '../components/ArticleBadges'
import { formatGewicht, formatPreis, formatSteuersatz, leseDezimal } from '../lib/article-format'
import type { Article, ArticleGroup, ListArticlesParams } from '../types/article.types'
import { ARTICLE_KINDS } from '../types/article.types'

const COLUMN_LAYOUT_KEY = 'columnbar_layout_articles'
const DEFAULT_FILTER_FIELD_KEYS = ['search', 'fulltext', 'kind', 'role', 'isActive']
const DEFAULT_COLUMN_KEYS = ['articleNumber', 'image', 'name', 'kind', 'group', 'unit', 'salesPrice', 'roles']
const EINKAUF = 'articles.purchase.view'

/**
 * Stand des gespeicherten Spaltenlayouts.
 *
 * **2** (13.9.2026, Muster AP-31): Die Artikelnummer ist keine feste erste Spalte
 * mehr, sondern eine wie jede andere — ausblendbar und verschiebbar. Ein Layout
 * aus der Zeit davor kennt sie nicht in `columnKeys`; ohne Hochstufung verlöre
 * jeder Benutzer mit gespeichertem Layout die Spalte beim ersten Öffnen.
 */
const LAYOUT_VERSION = 2

/** Was der Server sortieren kann (`ARTIKEL_SORTIERBAR`). */
const SERVER_SORTIERBAR = new Set(['articleNumber', 'name', 'kind', 'salesPrice', 'taxRate', 'createdAt', 'updatedAt'])

type Uebersetzer = (key: string) => string

const buildColumns = (t: Uebersetzer): ColumnFieldDef[] => [
  { key: 'articleNumber', label: t('articles.fields.articleNumber'), category: 'core' },
  { key: 'image', label: t('articles.fields.image'), category: 'core' },
  { key: 'name', label: t('articles.fields.name'), category: 'core' },
  // N-19 — standardmäßig aus.
  { key: 'ean', label: t('articles.fields.ean'), category: 'core' },
  { key: 'kind', label: t('articles.fields.kind'), category: 'core' },
  { key: 'group', label: t('articles.fields.group'), category: 'core' },
  { key: 'unit', label: t('articles.fields.unit'), category: 'core' },
  { key: 'salesPrice', label: t('articles.fields.salesPrice'), category: 'core', align: 'right' },
  { key: 'taxRate', label: t('articles.fields.taxRate'), category: 'core', align: 'right' },
  // Nachtrag N-7 — standardmäßig aus; eine neue, ausgeblendete Spalte braucht keine Layout-Version.
  { key: 'weightPerUnit', label: t('articles.fields.weightColumn'), category: 'core', align: 'right' },
  { key: 'roles', label: t('articles.fields.roles'), category: 'core' },
  { key: 'preferredSupplier', label: t('articles.fields.preferredSupplier'), category: 'core', requiresPermission: EINKAUF },
  { key: 'purchasePrice', label: t('articles.fields.purchasePrice'), category: 'core', align: 'right', requiresPermission: EINKAUF },
  { key: 'documentText', label: t('articles.fields.documentText'), category: 'core' },
  { key: 'internalNote', label: t('articles.fields.internalNote'), category: 'core' },
  { key: 'externalId', label: t('articles.fields.externalId'), category: 'core' },
  { key: 'isActive', label: t('articles.fields.status'), category: 'core' },
  { key: 'createdAt', label: t('articles.fields.createdAt'), category: 'core' },
  { key: 'updatedAt', label: t('articles.fields.updatedAt'), category: 'core' },
]

const buildFilterFields = (t: Uebersetzer, gruppen: ArticleGroup[], einkauf = true): FilterFieldDef[] => [
  { key: 'search', label: t('articles.fields.search'), type: 'text', category: 'core', operators: ['contains'] },
  // N-20, Muster AP-31: jedes Wort muss irgendwo vorkommen — auch in Belegtext, Notiz, EAN, Gruppe, Einheit.
  { key: 'fulltext', label: t('articles.fields.fulltext'), type: 'text', category: 'core', operators: ['contains'] },
  {
    key: 'kind',
    label: t('articles.fields.kind'),
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: ARTICLE_KINDS.map((k) => ({ value: k, label: t(`articles.kinds.${k}`) })),
  },
  {
    key: 'groupId',
    label: t('articles.fields.group'),
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: gruppen.map((g) => ({ value: g.id, label: g.name })),
  },
  {
    key: 'role',
    label: t('articles.fields.roles'),
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: [
      { value: 'sales', label: t('articles.role.sales') },
      { value: 'purchase', label: t('articles.role.purchase') },
    ],
  },
  {
    key: 'isActive',
    label: t('articles.fields.status'),
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: [
      { value: 'true', label: t('articles.status.active') },
      { value: 'false', label: t('articles.status.inactive') },
      { value: 'all', label: t('articles.status.all') },
    ],
  },
  { key: 'priceFrom', label: t('articles.fields.priceFrom'), type: 'number', category: 'core', operators: ['is'] },
  { key: 'priceTo', label: t('articles.fields.priceTo'), type: 'number', category: 'core', operators: ['is'] },
  // AP-40 7.5: die Filter hinter den Kacheln des Artikel-Dashboards.
  {
    key: 'purchaseState',
    label: t('articles.fields.purchaseState'),
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: [
      // Preis fehlt oder ist alt: nur mit Einkaufsrecht — die Frage verriete den Preis.
      ...(einkauf
        ? [
            { value: 'NO_PRICE', label: t('articles.purchaseState.NO_PRICE') },
            { value: 'OLD_PRICE', label: t('articles.purchaseState.OLD_PRICE') },
          ]
        : []),
      { value: 'NO_SUPPLIER', label: t('articles.purchaseState.NO_SUPPLIER') },
    ],
  },
  { key: 'priceBefore', label: t('articles.fields.priceBefore'), type: 'date', category: 'core', operators: ['is'], requiresPermission: EINKAUF },
  { key: 'changedFrom', label: t('articles.fields.changedFrom'), type: 'date', category: 'core', operators: ['is'] },
]

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
  version?: number
}

const STANDARD_LAYOUT: ColumnState = {
  columnKeys: DEFAULT_COLUMN_KEYS,
  columnWidths: {},
  sortKey: 'articleNumber',
  sortDir: 'asc',
  version: LAYOUT_VERSION,
}

/**
 * Ein gespeichertes Layout auf den aktuellen Stand heben — für den eigenen
 * Speicher **und** für den Mandanten-Standard, der auf demselben Weg kommt.
 */
function aufAktuellenStand(parsed: Partial<ColumnState> | null): ColumnState {
  if (!parsed?.columnKeys?.length) return STANDARD_LAYOUT
  const alt = (parsed.version ?? 1) < LAYOUT_VERSION
  return {
    columnKeys:
      alt && !parsed.columnKeys.includes('articleNumber') ? ['articleNumber', ...parsed.columnKeys] : parsed.columnKeys,
    columnWidths: parsed.columnWidths ?? {},
    sortKey: parsed.sortKey ?? STANDARD_LAYOUT.sortKey,
    sortDir: parsed.sortDir ?? 'asc',
    version: LAYOUT_VERSION,
  }
}

function loadColumnState(): ColumnState {
  return aufAktuellenStand(loadUiSetting<Partial<ColumnState> | null>(COLUMN_LAYOUT_KEY, null))
}

const datum = (iso: string) => new Date(iso).toLocaleDateString(navigator.language)

/** Filter → API-Parameter; geteilt von Liste und Export, damit beide dasselbe zeigen. */
function filterZuParametern(filters: Record<string, ActiveFilter>): ListArticlesParams {
  const params: ListArticlesParams = {}
  for (const [key, active] of Object.entries(filters)) {
    if (!active.value) continue
    if (key === 'search') params.search = active.value
    if (key === 'fulltext') params.fulltext = active.value
    if (key === 'kind') params.kind = active.value as ListArticlesParams['kind']
    if (key === 'groupId') params.groupId = active.value
    if (key === 'role') params.role = active.value as ListArticlesParams['role']
    if (key === 'isActive') params.isActive = active.value as ListArticlesParams['isActive']
    if (key === 'purchaseState') params.purchaseState = active.value as ListArticlesParams['purchaseState']
    if (key === 'priceBefore') params.priceBefore = active.value.slice(0, 10)
    if (key === 'changedFrom') params.changedFrom = active.value.slice(0, 10)
    if (key === 'priceFrom' || key === 'priceTo') {
      const zahl = leseDezimal(active.value)
      if (zahl !== null && !Number.isNaN(zahl)) params[key] = zahl
    }
  }
  return params
}

export default function ArticlesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user: currentUser } = useAuthStore()

  const darf = useCallback(
    (permission: string) => erfuelltRecht(permission, currentUser?.permissions),
    [currentUser?.permissions],
  )
  const canManage = darf('articles.article.manage')

  const [gruppen, setGruppen] = useState<ArticleGroup[]>([])
  const allColumns = ohneFehlendeRechte(buildColumns(t), darf)
  const filterFields = ohneFehlendeRechte(buildFilterFields(t, gruppen, darf(EINKAUF)), darf)

  // AP-40 6.4: Filter aus der Adresse (Kachel des Dashboards) — einmal beim Öffnen gelesen.
  const [suchParameter] = useSearchParams()
  const [adresse] = useState(() => filterAusAdresse(suchParameter, buildFilterFields(t, [])))
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(loadColumnState)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const dragKeyRef = useRef<string | null>(null)

  const [rows, setRows] = useState<Article[]>([])
  const [meta, setMeta] = useState({ total: 0, page: 1, totalPages: 1, limit: 50 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Referenzdaten getrennt laden — ein Fehler hier darf die Liste nicht leeren.
  useEffect(() => {
    articlesApi
      .groups()
      .then(({ data }) => setGruppen(data.data))
      .catch(() => undefined)
  }, [])

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, currentPage: number, state: ColumnState) => {
      setLoading(true)
      setError(null)
      try {
        const params: ListArticlesParams = {
          ...filterZuParametern(filters),
          page: currentPage,
          limit: 50,
          sortBy: state.sortKey,
          sortDir: state.sortDir,
        }
        const { data } = await articlesApi.list(params)
        setRows(data.data)
        setMeta(data.meta)
      } catch (err) {
        setError(t('common.error'))
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    fetchData(filterValues, page, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handleFiltersChange = useCallback(
    (newValues: Record<string, ActiveFilter>) => {
      setFilterValues(newValues)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => fetchData(newValues, 1, colState), 400)
    },
    [fetchData, colState],
  )

  // ── Spalten ─────────────────────────────────────────────────────────────

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(COLUMN_LAYOUT_KEY, next)
      return next
    })
  }, [])

  const handleSort = (key: string) => {
    if (!SERVER_SORTIERBAR.has(key)) return
    const newDir = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: newDir as 'asc' | 'desc' }
    updateColState(() => next)
    setPage(1)
    fetchData(filterValues, 1, next)
  }

  const handleDrop = (targetKey: string) => {
    const srcKey = dragKeyRef.current
    setDragOverKey(null)
    if (!srcKey || srcKey === targetKey) return
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const from = keys.indexOf(srcKey)
      const to = keys.indexOf(targetKey)
      if (from < 0 || to < 0) return prev
      keys.splice(from, 1)
      keys.splice(to, 0, srcKey)
      return { ...prev, columnKeys: keys }
    })
  }

  const resizeRef = useRef<{ key: string; startX: number; startW: number } | null>(null)
  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    resizeRef.current = { key, startX: e.clientX, startW: th?.getBoundingClientRect().width ?? 120 }
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      const newW = Math.max(40, resizeRef.current.startW + ev.clientX - resizeRef.current.startX)
      const k = resizeRef.current.key
      updateColState((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, [k]: newW } }))
    }
    const onUp = () => {
      resizeRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const shownColumns = colState.columnKeys
    .map((k) => allColumns.find((c) => c.key === k))
    .filter((c): c is ColumnFieldDef => c !== undefined)
  const availableToAdd = allColumns.filter((c) => !colState.columnKeys.includes(c.key))

  const sortSymbol = (key: string) =>
    colState.sortKey === key ? (
      colState.sortDir === 'asc' ? (
        <ArrowUp className="h-3 w-3 text-primary" />
      ) : (
        <ArrowDown className="h-3 w-3 text-primary" />
      )
    ) : (
      <ArrowUpDown className="h-3 w-3 opacity-30" />
    )

  // ── Zelle ───────────────────────────────────────────────────────────────

  const renderCell = (row: Article, col: ColumnFieldDef): React.ReactNode => {
    switch (col.key) {
      case 'articleNumber':
        return <span className="font-mono text-muted-foreground">{row.articleNumber}</span>
      case 'ean':
        return row.ean ? <span className="font-mono">{row.ean}</span> : '—'
      case 'image':
        return row.imageUrl ? (
          <img src={row.imageUrl} alt="" loading="lazy" className="h-8 w-8 rounded-md object-cover" />
        ) : (
          <span className="inline-grid h-8 w-8 place-items-center rounded-md bg-muted">
            <ArtSymbol kind={row.kind} />
          </span>
        )
      case 'name':
        return <span className={`font-medium ${row.isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{row.name}</span>
      case 'kind':
        return <ArtAbzeichen kind={row.kind} />
      case 'group':
        return row.group?.name ?? '—'
      case 'unit':
        return row.unit?.code ?? '—'
      case 'salesPrice':
        return formatPreis(row.salesPrice, row.priceUnit, row.unit?.code)
      case 'taxRate':
        return formatSteuersatz(row.taxRate)
      case 'weightPerUnit':
        return formatGewicht(row.weightPerUnit)
      case 'roles':
        return <RollenKuerzel sales={row.isSalesArticle} purchase={row.isPurchaseArticle} />
      case 'preferredSupplier':
        return row.preferredSupplier ? row.preferredSupplier.name : '—'
      case 'purchasePrice':
        return formatPreis(row.purchasePrice, row.purchasePriceUnit, row.unit?.code)
      case 'documentText':
        return row.documentText ? <span className="block max-w-xs truncate">{row.documentText}</span> : '—'
      case 'internalNote':
        return row.internalNote ? <span className="block max-w-xs truncate">{row.internalNote}</span> : '—'
      case 'externalId':
        return row.externalId ? `${row.externalId}${row.externalSource ? ` (${row.externalSource})` : ''}` : '—'
      case 'isActive':
        return (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              row.isActive
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {row.isActive ? t('articles.status.active') : t('articles.status.inactive')}
          </span>
        )
      case 'createdAt':
        return datum(row.createdAt)
      case 'updatedAt':
        return datum(row.updatedAt)
      default:
        return null
    }
  }

  const totalCols = shownColumns.length + (columnConfigMode && availableToAdd.length > 0 ? 1 : 0) + 1
  const anzahl = loading ? t('common.loading') : `${meta.total} ${t('articles.count_other')}`

  // ── Export — genau die sichtbaren Spalten mit den aktiven Filtern ────────
  const handleExport = (format: 'xlsx' | 'docx' | 'pdf', orientation?: 'portrait' | 'landscape') => {
    const filterInfo = Object.entries(filterValues)
      .filter(([, a]) => a.value)
      .map(([key, a]) => {
        const feld = filterFields.find((f) => f.key === key)
        const wert = feld?.options?.find((o) => o.value === a.value)?.label ?? a.value
        return `${feld?.label ?? key}: ${wert}`
      })
      .join(' · ')
    return articlesApi.exportRequest({
      ...filterZuParametern(filterValues),
      sortBy: colState.sortKey,
      sortDir: colState.sortDir,
      format,
      orientation,
      filterInfo: filterInfo || undefined,
      // Genau die sichtbaren Spalten — auch ohne Artikelnummer, wenn sie ausgeblendet ist.
      // Bild und interne Notiz schneidet der Server ohnehin heraus.
      columns: shownColumns.map((c) => ({ key: c.key, label: c.label })),
    })
  }

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('articles.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('articles.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {darf('articles.article.export') && (
            <ExportButton
              request={handleExport}
              defaultFilename="artikel"
              label={t('articles.actions.export')}
              mail={{ source: 'EXPORT_ARTICLES', title: t('mailExport.titles.EXPORT_ARTICLES'), subject: t('mailExport.titles.EXPORT_ARTICLES') }}
            />
          )}
          {darf('articles.article.import') && (
            <Link
              to="/articles/import"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground hover:bg-card"
            >
              <Upload className="h-4 w-4" />
              {t('articles.actions.import')}
            </Link>
          )}
          <Link
            to="/articles/stammlisten"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground hover:bg-card"
          >
            <ListTree className="h-4 w-4" />
            {t('articles.actions.lists')}
          </Link>
          {canManage && (
            <button
              type="button"
              onClick={() => navigate('/articles/neu')}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              + {t('articles.create')}
            </button>
          )}
        </div>
      </div>

      <FilterBar
        availableFields={filterFields}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey="articles"
        defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS}
        adresse={adresse}
      />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title={t('common.configureColumns', { defaultValue: 'Spalten konfigurieren' })}
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>

        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = aufAktuellenStand(value as Partial<ColumnState> | null)
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColState(next)
                fetchData(filterValues, 1, next)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {/* Die Artikelnummer ist seit Layout-Version 2 eine Spalte wie jede andere — ausblendbar. */}
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 font-medium text-foreground
                      ${col.align === 'right' ? 'text-right' : 'text-left'}
                      ${columnConfigMode ? 'cursor-grab' : ''}
                      ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => (dragKeyRef.current = col.key) : undefined}
                    onDragOver={
                      columnConfigMode
                        ? (e) => {
                            e.preventDefault()
                            setDragOverKey(col.key)
                          }
                        : undefined
                    }
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={
                      columnConfigMode
                        ? () => {
                            dragKeyRef.current = null
                            setDragOverKey(null)
                          }
                        : undefined
                    }
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      {SERVER_SORTIERBAR.has(col.key) ? (
                        <button type="button" onClick={() => handleSort(col.key)} className="flex items-center gap-1 hover:text-primary">
                          {col.label}
                          {sortSymbol(col.key)}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button
                          type="button"
                          onClick={() =>
                            updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))
                          }
                          className="ml-1 text-muted-foreground hover:text-destructive"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                      onMouseDown={(e) => handleResizeStart(e, col.key)}
                    />
                  </th>
                ))}

                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ {t('common.column', { defaultValue: 'Spalte' })}</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>

            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('articles.noArticles')}
                  </td>
                </tr>
              )}

              {rows.map((row, idx) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(`/articles/${row.id}`)}
                  className={`cursor-pointer border-b border-border transition-colors last:border-0 ${
                    idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                  } hover:bg-accent/30`}
                >
                  {shownColumns.map((col) => (
                    <td
                      key={col.key}
                      className={`whitespace-nowrap px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}
                    >
                      {renderCell(row, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}

              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  {/* Die Anzahl steht in der ersten sichtbaren Spalte, welche das auch ist. */}
                  {shownColumns.map((col, i) => (
                    <td key={col.key} className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">
                      {i === 0 ? anzahl : null}
                    </td>
                  ))}
                  <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">{shownColumns.length === 0 ? anzahl : null}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>
              {meta.page} / {meta.totalPages} ({meta.total})
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                ‹
              </button>
              <button
                type="button"
                disabled={page >= meta.totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
